import React, { useState, useEffect } from 'react'
import {
  Box,
  Text,
  Modal,
  Heading,
  PrimaryButton,
  Alert,
  Flex,
} from '@moonpig/launchpad-components'
import { TextInput } from '@moonpig/launchpad-forms'
import { useMutation } from '@moonpig/web-core-graphql'
import {
  IllustrationEcard,
  IllustrationEnvelope,
  IconSystemEnvelope,
} from '@moonpig/launchpad-assets'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { useAuthenticationLocaleText } from '../../text-localisation'
import { WizardForgottenPasswordGQL } from './types'
import {
  WizardForgottenPasswordMutation as WizardForgottenPasswordResult,
  WizardForgottenPasswordMutationVariables,
} from './__generated__/types'
import {
  trackForgotPasswordCancelModal,
  trackForgotPasswordEmailSent,
  trackForgotPasswordImpression,
  trackForgotPasswordRequestPasswordReset,
  trackForgotPasswordSuccessfulDismiss,
} from './tracking'

enum ModalStep {
  CONFIRM_RESET,
  EMAIL_SENT,
}

const Illustration: React.FC<{
  modalStep: ModalStep
}> = ({ modalStep }) => {
  return modalStep === ModalStep.CONFIRM_RESET ? (
    <IllustrationEcard width="100px" height="100px" />
  ) : (
    <IllustrationEnvelope width="100px" height="100px" />
  )
}

export const ForgotPasswordModal: React.FC<{
  isOpen: boolean
  onDismiss: () => void
  email: string
}> = ({ isOpen, email, onDismiss }) => {
  const [error, setError] = useState('')

  const [emailToAction, setEmailToAction] = useState(() => email || '')
  const [modalStep, setModalStep] = useState(ModalStep.CONFIRM_RESET)
  const localiseText = useAuthenticationLocaleText()

  useEffect(() => {
    setEmailToAction(email)
  }, [email])

  useEffect(() => {
    if (isOpen) {
      trackForgotPasswordImpression()
    }
  }, [isOpen])

  const [forgottenPassword, { client, loading: isLoading }] = useMutation<
    WizardForgottenPasswordResult,
    WizardForgottenPasswordMutationVariables
  >(WizardForgottenPasswordGQL, {
    onError: () => {
      const localisedError = localiseText('auth_wizard.generic_error')
      setError(localisedError)

      trackGAEvent({
        event: 'generic_error',
        content_data: {
          content_type:
            'account | reset password error | authentication wizard | reset password',
        },
        event_data: {
          category: 'account',
          action: 'reset password error',
          label: 'reset password',
          non_interaction: true,
          value: undefined,
        },
        error_data: {
          id: 0,
          message: localisedError,
        },
      })
    },
    onCompleted: async () => {
      /* istanbul ignore next */
      if (!client) {
        throw new Error('The Apollo client is undefined')
      }
      await client.clearStore()
    },
  })

  const headerText = (): string => {
    return modalStep === ModalStep.CONFIRM_RESET
      ? localiseText('auth_wizard.forgot_password_heading')
      : localiseText('auth_wizard.forgot_password_sent_heading')
  }

  const messageText = (): string => {
    return modalStep === ModalStep.CONFIRM_RESET
      ? localiseText('auth_wizard.forgot_password_message')
      : localiseText('auth_wizard.forgot_password_sent_message')
  }

  const actionText = (): string => {
    return modalStep === ModalStep.CONFIRM_RESET
      ? localiseText('auth_wizard.forgot_password_send_email')
      : localiseText('auth_wizard.forgot_password_sent_ok')
  }

  const handleDismiss = () => {
    setError('')
    onDismiss()
    if (modalStep === ModalStep.EMAIL_SENT) {
      trackForgotPasswordSuccessfulDismiss()
    } else {
      trackForgotPasswordCancelModal()
    }
    setModalStep(ModalStep.CONFIRM_RESET)
  }

  const handleOnClick = () => {
    if (modalStep === ModalStep.CONFIRM_RESET) {
      trackForgotPasswordRequestPasswordReset()
      onSendEmailClick()
    } else {
      handleDismiss()
    }
  }

  const onSendEmailClick = async () => {
    setError('')

    const response = await forgottenPassword({
      variables: { input: { email: emailToAction } },
    })

    if (response && response.data && response.data.forgottenPassword) {
      const result = response.data.forgottenPassword
      switch (result.__typename) {
        case 'ForgottenPasswordSent':
          setModalStep(ModalStep.EMAIL_SENT)
          trackForgotPasswordEmailSent()
          break
        case 'ForgottenPasswordInvalidEmail': {
          setError(result.message)
          break
        }
        /* istanbul ignore next*/
        default: {
          setError(localiseText('auth_wizard.generic_error'))
          break
        }
      }
    }
  }

  const handleOnChange = (value: string) => {
    if (!email) {
      setEmailToAction(value)
    }
  }

  return (
    <Modal
      label={localiseText('auth_wizard.forgot_password_message')}
      isOpen={isOpen}
      onDismiss={handleDismiss}
      hasCloseButton
      mobilePagePeek
    >
      <Box justifyContent="center" width={{ xs: '100vw', md: 1 }}>
        <Box>
          <Illustration modalStep={modalStep} />
        </Box>
        <Heading
          level="h2"
          typography={{ xs: 'typeMobileDisplay03', md: 'typeDesktopDisplay04' }}
          mb={6}
        >
          {headerText()}
        </Heading>
        <Text as="div" mb={6}>
          {messageText()}
        </Text>
        <Flex flexDirection={{ xs: 'column', md: 'row' }} mb={6}>
          <Box flexGrow={4}>
            <TextInput
              mb={{ xs: 6, md: 0 }}
              label=""
              name="forgotPassword"
              value={emailToAction}
              disabled={!!email}
              data-testid="txtForgotPassword"
              onChange={e => handleOnChange(e.target.value)}
              componentStart={
                <IconSystemEnvelope
                  width="28px"
                  height="28px"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '50%',
                    transform: 'translate(50%, -50%)',
                    opacity: 0.5,
                  }}
                />
              }
            />
          </Box>

          <Box
            flexGrow={1}
            textAlign="right"
            mt={{ xs: 0, md: 3 }}
            ml={{ xs: 0, md: 6 }}
          >
            <PrimaryButton
              width={1}
              onClick={handleOnClick}
              loading={isLoading}
              data-testid="btnForgotPasswordCta"
            >
              {actionText()}
            </PrimaryButton>
          </Box>
        </Flex>

        {error && (
          <Alert variant="error" mb={6}>
            {error}
          </Alert>
        )}
      </Box>
    </Modal>
  )
}
